import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  ChevronDownIcon,
  ChevronUpIcon,
  getDuration,
  Notification,
  NotificationBellIcon,
  NotificationCard,
  NotificationPlaceholder,
  Preloader,
  RawButton,
  Text,
  useForceIntervalUpdate,
} from '@common-fe/common-fe';

import routes from '@/common/routes';
import AppButton from '@/components/controls/AppButton';
import { useHistory } from '@/modules/core/hooks';
import NotificationModal from '@/modules/notification/NotificationDetailsModal/NotificationModal';

import { useNotifications } from '../hooks';
import { useNotificationCenterStore } from '../store';

import {
  CoveredBlock,
  DisabledNotificationButton,
  GradientBox,
  TitleWrapper,
  Wrapper,
} from './NotificationsList.styles';

interface Props {
  isMobileView?: boolean;
}
const NotificationsList: React.FC<Props> = ({ isMobileView }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const history = useHistory();

  const { t } = useTranslation();

  const [activeNotification, setActiveNotification] = useState<Notification | undefined>();
  const [openModal, setOpenModal] = useState<boolean>(false);
  const {
    data: notifications,
    isLoading,
    handleDismissById,
    isSuccess,
  } = useNotifications({
    isQached: true,
  });

  const dismissedNotificationsTotal = useNotificationCenterStore(
    (state) => state.dismissedNotificationsTotal
  );
  const [notificationsData, setFilteredNotifications] = useState<Notification[]>([]);
  useEffect(() => {
    if (isSuccess) {
      setFilteredNotifications(notifications);
    }
  }, [isSuccess, notifications]);

  const handleDismissNotification = useCallback(
    (id?: string) => {
      handleDismissById(id);
      if (currentIndex === notificationsData.length - 1) {
        setCurrentIndex(currentIndex - 1);
      }
    },
    [handleDismissById, notificationsData, currentIndex]
  );
  const renderKey = useForceIntervalUpdate(getDuration({ minutes: 1 }));
  const handleOpenNotifications = useCallback(() => {
    history.push(routes.NOTIFICATIONS);
  }, [history]);

  const total = useMemo(
    () => (dismissedNotificationsTotal || 0) + (notificationsData.length || 0),
    [dismissedNotificationsTotal, notificationsData.length]
  );

  const handleGoToPage = useCallback(
    (path?: string) => {
      const link = path || activeNotification?.page;

      if (link) {
        history.push(link);

        setOpenModal(false);
      }
    },
    [activeNotification, history]
  );
  if (isLoading) {
    return (
      <Box width="100%" height="100%" align="center" justify="center">
        <Preloader />
      </Box>
    );
  }
  if (!notificationsData || !notificationsData.length) {
    return (
      <Wrapper direction="column" pad={{ horizontal: 'l', top: 'm' }} data-testid="Notification-list-wrapper">
        <Box direction="row" margin={{ left: 'auto' }}>
          <Box direction="column">
            <Box
              direction={isMobileView ? 'column' : 'row'}
              justify="between"
              align={isMobileView ? 'start' : 'center'}
              pad={{ bottom: 's' }}
            >
              <TitleWrapper direction="row" align="center">
                <NotificationBellIcon size="20px" color="iconAccent" />
                <Text margin={{ left: 'spacing8' }} size="large" weight="bold" color="textTitle">
                  {t('Recent notifications')}
                </Text>
              </TitleWrapper>
              <Box>
                {/* {(!dismissedNotifications.length && !notificationsData.length) */}
                {!total ? (
                  <DisabledNotificationButton>{t('All notifications (0)')}</DisabledNotificationButton>
                ) : (
                  <AppButton
                    testId="all-notifications"
                    buttonType="quaternary"
                    style={{ minHeight: '25px' }}
                    onClick={handleOpenNotifications}
                  >
                    {t(`All notifications (${total})`)}
                  </AppButton>
                )}
              </Box>
            </Box>
            <Box fill direction="row" justify="end">
              <Box fill data-testid="list-notification" style={{ width: '470px' }}>
                <NotificationPlaceholder isWhite />
              </Box>
            </Box>
          </Box>
          <Box
            direction="column"
            justify="center"
            width={{ min: '40px' }}
            margin={{ left: isMobileView ? 'spacing8' : 'spacing12' }}
          >
            <RawButton disabled>
              <Box
                background="canvas"
                border={{ size: 'small', color: 'border1' }}
                round="button1Round"
                align="center"
                justify="center"
                width="40px"
                height="40px"
              >
                <ChevronUpIcon size="24px" color="iconPrimary" />
              </Box>
            </RawButton>
            <RawButton disabled>
              <Box
                background="canvas"
                border={{ size: 'small', color: 'border1' }}
                round="button1Round"
                width="40px"
                align="center"
                justify="center"
                height="40px"
              >
                <ChevronDownIcon size="24px" color="iconPrimary" />
              </Box>
            </RawButton>
          </Box>
        </Box>
      </Wrapper>
    );
  }

  return (
    <Wrapper
      direction="column"
      height={isMobileView ? '320px' : '258px'}
      pad={isMobileView ? { horizontal: 'spacing16' } : { right: 'spacing24' }}
      data-testid="Notification-list-wrapper"
      style={{ position: 'relative' }}
    >
      {currentIndex !== notificationsData.length - 1 && <CoveredBlock />}
      <Box direction="row">
        <Box direction="column" width="470px" margin={isMobileView ? undefined : { left: 'auto' }}>
          <GradientBox disabledGradient={isMobileView} pad={{ top: 'm' }}>
            <Box
              direction={isMobileView ? 'column' : 'row'}
              justify="between"
              align={isMobileView ? 'start' : 'center'}
              pad={{ bottom: 'm' }}
              style={{
                zIndex: 1000,
              }}
            >
              <TitleWrapper direction="row" align="center">
                <NotificationBellIcon size="18px" color="iconAccent" />
                <Text margin={{ left: 'spacing8' }} size="large" weight="bold" color="textTitle">
                  {t('Recent notifications')}
                </Text>
              </TitleWrapper>
              <AppButton
                testId="all-notifications"
                buttonType="quaternary"
                style={{ minHeight: '25px' }}
                onClick={handleOpenNotifications}
              >
                {t(`All notifications (${total})`)}
              </AppButton>
            </Box>
          </GradientBox>
          <Box fill direction="row" style={{ overflow: 'hidden' }}>
            <Box width="500px" key={renderKey} align="end" data-testid="list-notification" fill>
              {notificationsData.map((item, index) => (
                <NotificationCard
                  key={item.id}
                  notification={item}
                  index={index}
                  cardHeight={isMobileView ? 182 : 152}
                  currentIndex={currentIndex}
                  allowTrimNumber={isMobileView ? 3 : 2}
                  bannerStyle
                  dismissNotification={handleDismissNotification}
                  setActiveNotification={setActiveNotification}
                  setOpenModal={setOpenModal}
                  isPageLinkHidden={!item.page}
                  style={{ width: currentIndex !== index ? 'calc(100% - 20px)' : '100%' }}
                  onGoToPage={() => handleGoToPage(item.page)}
                />
              ))}
            </Box>
          </Box>
        </Box>

        {notificationsData && (
          <Box
            direction="column"
            justify="center"
            width={{ min: '40px' }}
            gap="spacing8"
            margin={{ left: isMobileView ? 'spacing8' : 'spacing12' }}
          >
            <RawButton disabled={!currentIndex} onClick={() => setCurrentIndex(currentIndex - 1)}>
              <Box
                background="canvas"
                border={{ size: 'small', color: 'border1' }}
                round="button1Round"
                align="center"
                justify="center"
                width="40px"
                height="40px"
              >
                <ChevronUpIcon size="24px" color="iconPrimary" />
              </Box>
            </RawButton>
            <RawButton
              disabled={currentIndex === notificationsData.length - 1}
              onClick={() => setCurrentIndex(currentIndex + 1)}
            >
              <Box
                background="canvas"
                border={{ size: 'small', color: 'border1' }}
                round="button1Round"
                width="40px"
                align="center"
                justify="center"
                height="40px"
              >
                <ChevronDownIcon size="24px" color="iconPrimary" />
              </Box>
            </RawButton>
          </Box>
        )}

        <NotificationModal
          notification={activeNotification}
          setOpenModal={setOpenModal}
          openModal={openModal}
          isPageLinkHidden={!activeNotification?.page}
          onGoToPage={handleGoToPage}
          dismissNotification={handleDismissNotification}
        />
      </Box>
    </Wrapper>
  );
};

export default NotificationsList;
